import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../app/AuthContext";
import { useGame } from "../app/GameContext";
import { errorMessage } from "../api/client";
import { exportSummary } from "../api/game";
import type { PlayerSettings } from "../shared/game-types";
import { ActionButton, ChoiceGroup, PageHeader, formatDurationWithReal } from "../components/GameUi";

export function SettingsPage() {
  const auth = useAuth();
  const { state, secondsPerGameYear, saveId } = useGame();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<PlayerSettings | null>(null);
  const [workspace, setWorkspace] = useState<"guide" | "preferences" | "security" | "data">("guide");
  const [civilizationName, setCivilizationName] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordFeedback, setPasswordFeedback] = useState<string | null>(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (!state) return;
    setSettings({ ...state.player.settings });
    setCivilizationName(state.player.civilizationName);
  }, [state]);
  if (!state || !settings) return null;

  async function submitPassword(event: React.FormEvent) {
    event.preventDefault();
    if (newPassword !== passwordConfirmation) {
      setPasswordFeedback("两次输入的新密码不一致。");
      return;
    }
    setPasswordBusy(true);
    setPasswordFeedback(null);
    try {
      await auth.changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setPasswordConfirmation("");
      setPasswordFeedback("密码已修改，其他设备的会话已撤销。");
    } catch (cause) {
      setPasswordFeedback(errorMessage(cause));
    } finally {
      setPasswordBusy(false);
    }
  }

  async function downloadExport() {
    setExportBusy(true);
    setExportError(null);
    try {
      const summary = await exportSummary(saveId);
      const url = URL.createObjectURL(new Blob([JSON.stringify(summary, null, 2)], { type: "application/json" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `stellar-colony-summary-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (cause) {
      setExportError(errorMessage(cause));
    } finally {
      setExportBusy(false);
    }
  }

  async function deleteAccount(event: React.FormEvent) {
    event.preventDefault();
    if (deleteConfirmation !== "DELETE") {
      setDeleteError("请输入大写 DELETE 以确认。");
      return;
    }
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await auth.removeAccount(deletePassword);
      navigate("/register", { replace: true });
    } catch (cause) {
      setDeleteError(errorMessage(cause));
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <div className="page-stack">
      <PageHeader eyebrow="SYSTEM // ACCOUNT CONTROL" title="设置" description="显示偏好随存档同步；安全操作会重新验证密码，并由服务器撤销相关会话。" />
      <div className="focus-picker">
        <ChoiceGroup label="设置类别" value={workspace} onChange={setWorkspace} variant="cards" options={[
          { value: "guide", label: "游戏文件", description: "机制与长期规划" },
          { value: "preferences", label: "显示与标识", description: "界面偏好和文明名称" },
          { value: "security", label: "账号安全", description: "密码与当前会话" },
          { value: "data", label: "数据管理", description: "导出与删除账号" },
        ]} />
        <p>{settingsWorkspaceDescriptions[workspace]}</p>
      </div>
      <div className="settings-grid">
        <section className="panel game-guide" aria-labelledby="guide-heading" hidden={workspace !== "guide"}>
          <div className="section-heading">
            <div><p className="eyebrow">GAME FILE // MECHANICS</p><h2 id="guide-heading">游戏文件：机制与长期规划</h2></div>
            <span className="status-chip">{formatDurationWithReal(1, secondsPerGameYear)}</span>
          </div>
          <p>这是一款以挂机推进、每日约两次上线规划为核心的硬核殖民游戏。服务器保存权威时间与存档；你负责安排研究、建设、远征和重大决策。</p>

          <details>
            <summary>时间、挂机报告与上线节奏</summary>
            <div className="guide-content">
              <p>关闭网页不会暂停文明。下次登录时会先看到一份挂机报告；服务器按真实经过时间推进生产、维护、研究、建设、航行、贸易和事件，不依赖浏览器后台运行。</p>
              <ul>
                <li>离线时长没有硬性截断；每个星球的能源、工程材料仓库和全局科研点都有容量上限，满仓后对应产出不再累积。</li>
                <li>研究队列、建设和航行会继续完成；需要选择的重大事件会停在待决列表，不会替你作不可逆决定。</li>
                <li>相邻两次 12 小时登录约推进 24 游戏年。顶部时钟只做实时插值，资源结算仍以服务器为准。</li>
              </ul>
            </div>
          </details>

          <details>
            <summary>六项文明能力</summary>
            <div className="guide-content">
              <dl className="guide-definitions">
                <div><dt>文明发展</dt><dd>工业、航行、军事、能源和材料五项能力的综合阶段。</dd></div>
                <div><dt>工业能力</dt><dd>决定设施、船厂和制造规模；完成工业研究也会同步推动能源与材料能力。</dd></div>
                <div><dt>航行能力</dt><dd>决定推进方式、任务速度和可到达的恒星系；每提升 1级，普通与重型运输船的有效货舱都增加 20%，3、5、7级还会稳定发现新的星系。</dd></div>
                <div><dt>军事能力</dt><dd>决定舰船解锁、战斗系统和有限战争能力。</dd></div>
                <div><dt>能源能力</dt><dd>代表可用燃料与发电技术，也是高级设施和科技的门槛。</dd></div>
                <div><dt>材料能力</dt><dd>代表耐高温、核、超导和稀有工程材料的制造水平。</dd></div>
              </dl>
            </div>
          </details>

          <details>
            <summary>资源、产能与自动降载</summary>
            <div className="guide-content">
              <ul>
                <li>能源与工程材料按星球独立存放。建设、开发、造船和维修只使用所在地库存，缺口必须由运输船补给。</li>
                <li>化学、裂变、聚变、反物质燃料与推进剂由文明专用物流网统一管理；玩家运输任务只装载可用能源和工程材料。</li>
                <li>结构、高温、核、超导、稀有材料来自不同天体，矿藏会从高峰产量逐步衰减至稳定产量。</li>
                <li>科研点在研究开始时一次性支付。能源不足时，系统按你设置的优先级自动降载，避免库存被扣成负数。</li>
              </ul>
            </div>
          </details>

          <details>
            <summary>科研曲线、队列与取消规则</summary>
            <div className="guide-content">
              <p>研究只有一个执行槽，最多可预排 20 项。项目从 1 游戏年（现实约 30分钟）逐步增长到 48 游戏年（现实约 1天）；三条分支的纯计时下限约 15.7 天，资源与前置会把完整进程延长到数周。</p>
              <ul>
                <li>工业、航行、军事三线可以交错规划，没有永久互斥。</li>
                <li>轮到队首项目启动时若科研点、能力等级或前置不足，它会保留在队首，等待下次结算。</li>
                <li>取消当前研究返还 50% 已投入科研点；长项目开始前请确认队列顺序。</li>
              </ul>
            </div>
          </details>

          <details>
            <summary>天体参数与开发阶段</summary>
            <div className="guide-content">
              <dl className="guide-definitions">
                <div><dt>环境难度</dt><dd>越高越难维护，开发周期和设施损耗也越大。</dd></div>
                <div><dt>资源质量</dt><dd>决定开采高峰与长期稳定产量。</dd></div>
                <div><dt>殖民适宜度</dt><dd>2级以上固态天体才适合永久殖民。</dd></div>
                <div><dt>开发阶段</dt><dd>0级未探明，1级初步探测，2级轨道勘测，3级自动据点，4级工业基地，5级永久殖民地，6级成熟核心世界。普通建设可推进 2级至4级和 5级至6级；永久殖民地必须由殖民船建立。</dd></div>
              </dl>
              <p>所有天体操作都在星图中完成：未勘测目标只显示勘测入口；完成后可查看当地库存、产量和建筑，并在“开发建设”或“运输补给”中下达命令。同一天体的设施按队列串行建设，不同节点可以并行。</p>
            </div>
          </details>

          <details>
            <summary>舰队、外交、事件与战斗</summary>
            <div className="guide-content">
              <ul>
                <li>所有未被消耗的飞船抵达后都会停驻目标天体，不会自动返回家园；两类运输船都可装载当地产物，也可空载调动。</li>
                <li>重型运输船在工业与航行能力 5级解锁：货舱是普通运输船的 5倍、同级航速为三分之一，适合每日低频的大宗补给；普通运输船更适合紧急和高频航线。</li>
                <li>舰船名册可让任何停驻外地的舰船返回家园。推进剂不足时会启动应急返航：耗尽剩余推进剂并将航程延长一倍，抵达后需维修补给。</li>
                <li>发现外星文明后还需完成观测和正式接触，才能贸易或宣告有限战争。</li>
                <li>战斗依次结算探测、拦截、远程、近距和撤退五阶段；战前命令会影响结果与外交后果。</li>
              </ul>
            </div>
          </details>

          <details>
            <summary>长期完成目标</summary>
            <div className="guide-content">
              <p>长期目标是完成全部 30 项科技、观测 5 个恒星系，并在母星系外建立 3 个永久殖民地。科技只是骨架；真正的完成需要稳定能源、跨星系物流、外交关系和可持续工业网络。</p>
            </div>
          </details>
        </section>

        <section className="panel" aria-labelledby="display-heading" hidden={workspace !== "preferences"}>
          <div className="section-heading"><div><p className="eyebrow">INTERFACE</p><h2 id="display-heading">显示与确认</h2></div></div>
          <div className="form-stack">
            <ChoiceGroup label="信息密度" value={settings.density} onChange={(density) => setSettings({ ...settings, density })} options={[
              { value: "comfortable", label: "舒适", description: "更多留白，适合首次游玩" },
              { value: "compact", label: "紧凑", description: "同屏显示更多信息" },
            ]} />
            <label className="check-row"><input type="checkbox" checked={settings.confirmDangerous} onChange={(event) => setSettings({ ...settings, confirmDangerous: event.target.checked })} /><span><b>危险操作二次确认</b><small>战争、殖民和取消项目等操作</small></span></label>
            <label className="check-row"><input type="checkbox" checked={settings.reducedMotion} onChange={(event) => setSettings({ ...settings, reducedMotion: event.target.checked })} /><span><b>减少动态效果</b><small>同时尊重系统 prefers-reduced-motion</small></span></label>
            <ActionButton className="button primary" action={{ type: "CHANGE_SETTINGS", settings }}>保存显示设置</ActionButton>
          </div>
        </section>

        <section className="panel" aria-labelledby="identity-heading" hidden={workspace !== "preferences"}>
          <div className="section-heading"><div><p className="eyebrow">IDENTITY</p><h2 id="identity-heading">文明标识</h2></div></div>
          <div className="form-stack">
            <label><span className="field-label">文明名称</span><input maxLength={48} value={civilizationName} onChange={(event) => setCivilizationName(event.target.value)} /></label>
            <ActionButton className="button primary" action={{ type: "RENAME_CIVILIZATION", name: civilizationName.trim() }} disabled={civilizationName.trim().length < 2 || civilizationName.trim() === state.player.civilizationName}>更新名称</ActionButton>
            <p className="field-hint">显示名称不影响世界种子、存档 ID 或外交历史。</p>
          </div>
        </section>

        <section className="panel" aria-labelledby="password-heading" hidden={workspace !== "security"}>
          <div className="section-heading"><div><p className="eyebrow">SECURITY</p><h2 id="password-heading">修改密码</h2></div></div>
          <form className="form-stack" onSubmit={submitPassword}>
            {passwordFeedback && <div className="alert" role="status">{passwordFeedback}</div>}
            <label><span className="field-label">当前密码</span><input type="password" autoComplete="current-password" required value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} /></label>
            <label><span className="field-label">新密码</span><input type="password" autoComplete="new-password" required minLength={10} maxLength={128} value={newPassword} onChange={(event) => setNewPassword(event.target.value)} /></label>
            <label><span className="field-label">确认新密码</span><input type="password" autoComplete="new-password" required minLength={10} maxLength={128} value={passwordConfirmation} onChange={(event) => setPasswordConfirmation(event.target.value)} /></label>
            <button className="button primary" disabled={passwordBusy}>{passwordBusy ? "更新中…" : "修改密码并撤销其他会话"}</button>
          </form>
        </section>

        <section className="panel" aria-labelledby="sessions-heading" hidden={workspace !== "security"}>
          <div className="section-heading"><div><p className="eyebrow">SESSIONS</p><h2 id="sessions-heading">当前会话</h2></div></div>
          <ul className="data-list session-list">{auth.sessions.map((session) => <li key={session.id}><span><b>{session.current ? "本设备" : "其他设备"}</b><small>最近活动 {new Date(session.lastSeenAt).toLocaleString("zh-CN")}</small></span><span>{new Date(session.expiresAt).toLocaleDateString("zh-CN")} 到期</span></li>)}</ul>
          <p className="field-hint">恢复码只在注册或恢复成功时显示一次。若需要恢复账号，请从<Link to="/login">登录页的恢复入口</Link>操作。</p>
        </section>

        <section className="panel" aria-labelledby="export-heading" hidden={workspace !== "data"}>
          <div className="section-heading"><div><p className="eyebrow">READ-ONLY EXPORT</p><h2 id="export-heading">导出存档摘要</h2></div></div>
          <p>下载当前文明、资源、等级与任务的只读 JSON 摘要。该文件不包含密码、会话或可导入的权威存档。</p>
          {exportError && <div className="alert danger" role="alert">{exportError}</div>}
          <button className="button" onClick={() => void downloadExport()} disabled={exportBusy}>{exportBusy ? "正在生成…" : "下载 JSON 摘要"}</button>
        </section>

        <section className="panel danger-zone" aria-labelledby="delete-heading" hidden={workspace !== "data"}>
          <div className="section-heading"><div><p className="eyebrow">IRREVERSIBLE</p><h2 id="delete-heading">删除账号与存档</h2></div></div>
          <p>此操作会级联删除账号、全部会话、权威游戏存档和操作收据，无法恢复。</p>
          <form className="form-stack" onSubmit={deleteAccount}>
            {deleteError && <div className="alert danger" role="alert">{deleteError}</div>}
            <label><span className="field-label">当前密码</span><input type="password" autoComplete="current-password" required value={deletePassword} onChange={(event) => setDeletePassword(event.target.value)} /></label>
            <label><span className="field-label">输入 DELETE</span><input required autoComplete="off" value={deleteConfirmation} onChange={(event) => setDeleteConfirmation(event.target.value)} /></label>
            <button className="button danger-button" disabled={deleteBusy || deleteConfirmation !== "DELETE"}>{deleteBusy ? "正在删除…" : "永久删除账号"}</button>
          </form>
        </section>
      </div>
    </div>
  );
}

const settingsWorkspaceDescriptions = {
  guide: "阅读时间、资源、研究、开发、舰队与完成目标的完整规则。",
  preferences: "调整信息密度、动态效果、危险操作确认和文明名称。",
  security: "修改密码并查看仍然有效的登录会话。",
  data: "下载只读存档摘要，或执行不可恢复的账号删除。",
} as const;
